/**
 * BasicInfoContext - State management for Basic Info medical history flow
 * Provides shared state across all Basic Info screens and handles API submission
 */

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { BasicInfoFlowRoute, BasicInfoState } from "./types";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { Patient } from "./calculateBasicInfoCompletion";
import { buildBasicInfoNavigationStack } from "./buildBasicInfoNavigationStack";
import { getQuickExposureSubstances } from "./quickExposure";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { getBasicInfoMedicationState } from "./getBasicInfoMedicationState";

interface BasicInfoContextValue {
  state: BasicInfoState;
  updateState: (updates: Partial<BasicInfoState>) => void;
  resetState: () => void;
}

const BasicInfoContext = createContext<BasicInfoContextValue | undefined>(
  undefined,
);

const initialState: BasicInfoState = {
  sex: null,
  height: null,
  weight: null,
  dateOfBirth: null,
  quickExposureSubstances: null,
  hasBeenPregnant: null,
  pregnancyCount: null,
  hasEndometriosis: null,
  suspectPCOS: null,
  pcosDiagnosed: null,
  hadVaricoceles: null,
  hasCancerHistory: null,
  hasAllergies: null,
  allergies: [],
  currentlyTakingMedications: null,
  medications: [],
  navigationStack: [{ name: "SexAtBirth" }],
  currentNavigationIndex: 0,
};

export function BasicInfoProvider({ children }: { children: ReactNode }) {
  const { data: patient } = usePatient();

  const [state, setState] = useState<BasicInfoState>(initialState);

  useEffect(() => {
    if (!patient) {
      return;
    }
    setState((prev) => getBasicInfoState(patient, prev));
  }, [patient]);

  const updateState = useCallback((updates: Partial<BasicInfoState>) => {
    setState((prev) => {
      const newState = { ...prev, ...updates };
      const effectiveGender =
        (newState.sex as "AFAB" | "AMAB" | null | undefined) ?? null;
      const stack = buildBasicInfoNavigationStack(newState, effectiveGender);
      return {
        ...newState,
        navigationStack: stack,
        currentNavigationIndex: Math.min(
          newState.currentNavigationIndex,
          Math.max(stack.length - 1, 0),
        ),
      };
    });
  }, []);

  const resetState = useCallback(
    (state?: BasicInfoState) => setState(state ?? initialState),
    [],
  );

  return (
    <BasicInfoContext.Provider value={{ state, updateState, resetState }}>
      {children}
    </BasicInfoContext.Provider>
  );
}

/**
 * Hook to use BasicInfo state in any screen
 * @throws Error if used outside BasicInfoProvider
 */
export function useBasicInfo(currentScreen: BasicInfoFlowRoute) {
  const context = useContext(BasicInfoContext);
  const navigation =
    useNavigation<NavigationProp<Record<string, object | undefined>>>();

  if (!context) {
    throw new Error("useBasicInfo must be used within BasicInfoProvider");
  }

  const { state: fullState, updateState, resetState } = context;
  const { navigationStack, currentNavigationIndex, ...state } = fullState;
  const questionStack = navigationStack.filter(
    (entry) => entry.name !== "AboutYouComplete",
  );
  const questionIndex = questionStack.findIndex(
    (entry) => entry.name === currentScreen,
  );
  const progressPercent =
    questionIndex >= 0 && questionStack.length > 0
      ? (questionIndex + 1) / questionStack.length
      : undefined;

  const next = useCallback(
    (stateUpdates: Partial<BasicInfoState> = {}) => {
      const projectedState = { ...fullState, ...stateUpdates };
      const effectiveGender =
        (projectedState.sex as "AFAB" | "AMAB" | null | undefined) ?? null;
      const hasStateUpdates = Object.keys(stateUpdates).length > 0;
      const stack =
        hasStateUpdates || !navigationStack?.length
          ? buildBasicInfoNavigationStack(projectedState, effectiveGender)
          : navigationStack;

      const currentIndex = stack.findIndex(
        (entry) => entry.name === currentScreen,
      );
      if (currentIndex < 0) {
        return;
      }

      const nextIndex = Math.min(currentIndex + 1, stack.length - 1);
      const nextRoute = stack[nextIndex];

      updateState({
        ...stateUpdates,
        navigationStack: stack,
        currentNavigationIndex: nextIndex,
      });

      if (nextRoute) {
        navigation.navigate(nextRoute.name, nextRoute.params);
      }
    },
    [currentScreen, navigationStack, fullState, updateState, navigation],
  );

  return { state, updateState, resetState, next, progressPercent };
}

function getBasicInfoState(
  patient: Patient | undefined,
  previousState: BasicInfoState,
): BasicInfoState {
  const body = patient?.bodyData;
  const obgyn = patient?.obGynHistory;
  const med = patient?.medicalHistory;

  const next: Partial<BasicInfoState> = {};

  if (!previousState.sex && patient?.gender) {
    next.sex = patient.gender;
  }

  // Only bootstrap fields that are empty in previousState
  if (!previousState.dateOfBirth && patient?.dateOfBirth) {
    next.dateOfBirth = patient.dateOfBirth;
  }
  if (!previousState.height && body?.height != null) {
    next.height = body.height;
  }
  if (!previousState.weight && body?.weight != null) {
    next.weight = body.weight;
  }

  if (previousState.quickExposureSubstances == null) {
    const quickExposureSubstances = getQuickExposureSubstances(patient);
    if (quickExposureSubstances != null) {
      next.quickExposureSubstances = quickExposureSubstances;
    }
  }

  if (previousState.hasBeenPregnant == null && obgyn?.hasBeenPregnant != null) {
    next.hasBeenPregnant = obgyn.hasBeenPregnant;
  }

  // if pregnancies known, set count
  if (
    previousState.pregnancyCount == null &&
    Array.isArray(obgyn?.pregnancies) &&
    obgyn?.pregnancies.length > 0
  ) {
    next.pregnancyCount = obgyn.pregnancies.length;
  }

  if (previousState.hasEndometriosis == null && med?.hasEndometriosis != null) {
    next.hasEndometriosis = med.hasEndometriosis;
  }

  if (previousState.suspectPCOS == null && med?.suspectPCOS != null) {
    next.suspectPCOS = med.suspectPCOS;
  }

  // Check if PCOS is diagnosed (in diseases table)
  if (previousState.pcosDiagnosed == null) {
    const hasPCOSDisease =
      med?.diseases?.some((d) => d.disease === "pcos") ?? false;
    if (hasPCOSDisease) {
      next.pcosDiagnosed = true;
      // If diagnosed, suspectPCOS should be false
      if (previousState.suspectPCOS == null) {
        next.suspectPCOS = false;
      }
    }
  }

  if (previousState.hadVaricoceles == null && med?.hadVaricoceles != null) {
    next.hadVaricoceles = med.hadVaricoceles;
  }

  if (previousState.hasCancerHistory == null && med?.hasCancerHistory != null) {
    next.hasCancerHistory = med.hasCancerHistory;
  }

  if (previousState.hasAllergies == null && med?.hasAllergies != null) {
    next.hasAllergies = med.hasAllergies;
  }

  if (
    (!previousState.allergies || previousState.allergies.length === 0) &&
    Array.isArray(med?.allergies) &&
    med?.allergies.length > 0
  ) {
    next.allergies = med.allergies;
  }

  Object.assign(next, getBasicInfoMedicationState(previousState, med));

  const hydratedState = {
    ...previousState,
    ...next,
  };
  const effectiveGender =
    (hydratedState.sex as "AFAB" | "AMAB" | null | undefined) ??
    patient?.gender ??
    null;
  const stack = buildBasicInfoNavigationStack(hydratedState, effectiveGender);

  return {
    ...previousState,
    ...next,
    navigationStack: stack,
    currentNavigationIndex: Math.min(
      previousState.currentNavigationIndex ?? 0,
      Math.max(stack.length - 1, 0),
    ),
  };
}
